import React from 'react';
import { Box, Typography } from '@mui/material';
import { needsPriceTypeLabel, resolveTaskPrices } from 'core/slices/utils/priceTasks';
import {
  getAisleDisplay, getOperationalIdentifier, CardRow, StatusBadge, titleSx, captionSx,
  getOverheadDisplay, getOverheadLabel, OverheadValue, getDetectedDisplay, DetectedValue,
} from '../taskUtils';

/**
 * List card for price_mismatch tasks.
 *
 * A shelf label can carry several prices. One price shows the read/POS pair
 * directly; two or more collapse into a count, because there is no room for
 * several pairs and the detail view holds the breakdown. Stock is left out: it
 * says nothing about a price mismatch, and the generic card only showed it
 * because this task type had no card of its own.
 */
export default function PriceMismatch({ task, t, brandDict, completed }) {
  const { description, product_description, status } = task;
  const brandName = brandDict?.[task.brand_id];
  const { title: aisleTitle, value: aisleValue } = getAisleDisplay(task, t);
  const identifier = getOperationalIdentifier(task, t);
  const overheadDisplay = getOverheadDisplay(task, t);
  const detectedDisplay = getDetectedDisplay(task, t);

  const prices = resolveTaskPrices(task);
  const singlePrice = prices.length === 1 ? prices[0] : null;
  // A lone promotional price is indistinguishable from a regular one without
  // this, and it is the frequent case in stores that run promotions.
  const priceTypeLabel = needsPriceTypeLabel(task)
    ? t(`bruno.price_type.${singlePrice.price_type}`, singlePrice.price_type)
    : null;

  return (
    <Box sx={{ minWidth: 0, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'nowrap' }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ ...titleSx, flexShrink: 1, minWidth: 0 }}>
          {description || product_description || '—'}
        </Typography>
        {completed && <StatusBadge status={status} label={t(`bruno.task_status.${status}`, status)} />}
      </Box>
      {brandName && (
        <Typography variant="caption" color="text.disabled" display="block" sx={captionSx}>
          {brandName}
        </Typography>
      )}
      <CardRow label={aisleTitle} value={aisleValue} />
      <CardRow label={identifier.label} value={identifier.value} />
      {overheadDisplay && (
        <CardRow label={getOverheadLabel(t)} value={<OverheadValue display={overheadDisplay} dense />} />
      )}
      {detectedDisplay && (
        <CardRow label={detectedDisplay.label} value={<DetectedValue display={detectedDisplay} dense />} />
      )}

      {prices.length > 1 ? (
        <CardRow
          label={t('bruno.tasks_details.prices', 'Prices')}
          value={t('bruno.tasks.price_mismatches', { count: prices.length })}
          valueColor="error.main"
        />
      ) : singlePrice ? (
        <>
          <CardRow
            label={t('bruno.tasks_details.price', 'Price')}
            value={singlePrice.price != null ? singlePrice.price : '-'}
            valueColor="error.main"
          />
          <CardRow
            label={t('bruno.tasks_details.pos_price', 'POS Price')}
            value={singlePrice.pos_price != null ? singlePrice.pos_price : '-'}
            valueColor="success.main"
          />
          {priceTypeLabel && (
            <CardRow label={t('bruno.tasks_details.price_type', 'Price type')} value={priceTypeLabel} />
          )}
        </>
      ) : null}
    </Box>
  );
}
